import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Check } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import type { ProjectResponse, TaskDetail } from '@mybusinessmate/schema';
import { useAssetUploads } from '../../lib/use-asset-uploads';
import { PRIORITY_OPTIONS } from '../../lib/task-display';
import {
  emptyTaskForm,
  formFromTask,
  taskBodyFrom,
  toTaskRequest,
  type TaskFormErrors,
  type TaskFormValues,
  withType,
} from '../../lib/task-form';
import { useCreateTask, useUpdateTask } from '../../lib/tasks-api';
import { PageHeader } from '../shell/page-header';
import { Icon } from '../Icon';
import { Banner } from '../ui/banner';
import { Card } from '../ui/card';
import { Chip } from '../ui/chip';
import { DateField } from '../ui/date-field';
import { NumberStepper } from '../ui/number-stepper';
import { SegmentedControl } from '../ui/segmented-control';
import { pillClass } from '../ui/toolbar-controls';
import { Toggle } from '../ui/toggle';
import { useToast } from '../ui/toast';
import { AssetUploader } from './asset-uploader';
import { BriefForm } from './brief-form';
import { MilestoneEditor } from './milestone-editor';
import { NewTaskTeam } from './new-task-team';
import { TaskTypePicker } from './task-type-picker';

const ASSET_HINTS: Record<TaskFormValues['type'], string> = {
  post: 'Photos, logo and references for the team. The client can add more from their app.',
  reel: 'Raw footage, b-roll, logo and cover ideas. The client can add more from their app.',
  website: 'Photos, logo and references for the team. The client can add more from their app.',
  monthly_update: 'Screenshots, price lists, copy docs. The client can add more from their app.',
  custom: 'Anything the team should look at. The client can add more from their app.',
};

/** A numbered card title (Figma A05.3): a dark disc with the step, the title, one line of help, and an aside. */
export function StepHeading({
  step,
  title,
  description,
  aside,
}: {
  step: number;
  title: string;
  description?: string;
  aside?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-(--mbm-space-3)">
      <div className="flex items-start gap-(--mbm-space-2-5)">
        <span
          aria-hidden
          className="flex size-(--mbm-size-avatar-xxs) shrink-0 items-center justify-center rounded-full bg-action-primary text-label-small text-text-on-dark"
        >
          {step}
        </span>
        <div className="flex flex-col gap-(--mbm-space-0-5)">
          <h2 className="text-heading-h3 text-text-primary">{title}</h2>
          {description ? <p className="text-body-small text-text-secondary">{description}</p> : null}
        </div>
      </div>
      {aside}
    </div>
  );
}

function ToggleRow({
  title,
  hint,
  checked,
  onChange,
}: {
  title: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-(--mbm-space-4)">
      <div className="flex flex-col">
        <span className="text-label-medium text-text-primary">{title}</span>
        <span className="text-body-xsmall text-text-tertiary">{hint}</span>
      </div>
      <Toggle checked={checked} onCheckedChange={onChange} aria-label={title} />
    </div>
  );
}

export interface TaskFormPageProps {
  project: Pick<ProjectResponse, 'id' | 'name' | 'businessId' | 'businessName'>;
  /** Set when finishing a saved draft; the type can then no longer change. */
  existing?: TaskDetail;
  initialType?: TaskFormValues['type'];
}

/**
 * The new-task page (Figma A05.3, "You're writing the brief (v0) on the client's behalf"): kind of task, the brief,
 * triage, and what the client sees. Creating it freezes the brief as version 0; a draft can be saved and finished later.
 *
 * TODO(api): the design also has a "Team" panel on this page. People are added from the task page right after it is
 * created (the team picker and the pending-approval model live there); creating with a team in one request needs
 * `assigneeIds` on the create body. Also not available yet: per-plan usage on the type tiles, the "over this cycle's allowance" acknowledgement, and
 * billing for add-on orders (no allowance or order data in the API).
 */
export function TaskFormPage({ project, existing, initialType = 'post' }: TaskFormPageProps) {
  const businessId = project.businessId ?? '';
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateTask();
  const update = useUpdateTask(existing?.id ?? '');
  const uploads = useAssetUploads(businessId, existing?.brief?.assets ?? []);
  const [values, setValues] = useState<TaskFormValues>(() =>
    existing ? formFromTask(existing) : emptyTaskForm(initialType),
  );
  const [errors, setErrors] = useState<TaskFormErrors>({});
  const [formError, setFormError] = useState<string>();

  const set = (patch: Partial<TaskFormValues>) => setValues((v) => ({ ...v, ...patch }));
  const pending = create.isPending || update.isPending;

  const save = async (draft: boolean) => {
    setFormError(undefined);
    if (uploads.busy) return setFormError('Wait for the files to finish uploading.');
    if (uploads.failed) return setFormError('A file didn’t upload. Retry it or remove it first.');

    const result = toTaskRequest(values, project.id, uploads.assets, draft);
    if (!result.ok) {
      setErrors(result.errors);
      setFormError('Check the highlighted fields.');
      return;
    }
    setErrors({});

    try {
      const task = existing
        ? await update.mutateAsync(taskBodyFrom(values, uploads.assets, draft))
        : await create.mutateAsync(result.body);
      toast({
        title: draft ? 'Saved as a draft' : existing ? 'Task submitted' : 'Task created',
        description: task.title,
      });
      await navigate({ to: '/subdomain/admin/tasks/$taskId', params: { taskId: task.id } });
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.kind === 'validation' && e.fieldErrors?.length) {
        setErrors(Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])));
        setFormError('Check the highlighted fields.');
      } else if (e.kind === 'forbidden') {
        setFormError('You can’t create tasks for this business.');
      } else if (e.kind === 'not_found') {
        setFormError('This project no longer exists.');
      } else if (e.kind === 'unauthorized') {
        setFormError('Your session expired. Please sign in again.');
      } else if (e.status === 409) {
        setFormError(e.message ?? 'This task has already been submitted, so its brief is locked.');
      } else {
        setFormError('We couldn’t save the task. Please try again.');
      }
    }
  };

  return (
    <>
      <nav
        aria-label="Breadcrumb"
        className="flex flex-wrap items-center gap-(--mbm-space-2) text-label-medium text-text-secondary"
      >
        <Link
          to="/subdomain/admin/businesses"
          search={{ tab: undefined, q: undefined, am: undefined }}
          className="inline-flex items-center gap-(--mbm-space-2) hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          <Icon icon={ArrowLeft} size="sm" />
          Businesses
        </Link>
        {project.businessId ? (
          <>
            <span aria-hidden>/</span>
            <Link
              to="/subdomain/admin/businesses/$businessId"
              params={{ businessId: project.businessId }}
              className="hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              {project.businessName ?? 'Business'}
            </Link>
          </>
        ) : null}
        <span aria-hidden>/</span>
        <Link
          to="/subdomain/admin/projects/$projectId"
          params={{ projectId: project.id }}
          search={{}}
          className="hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
        >
          {project.name}
        </Link>
        <span aria-hidden>/</span>
        <span className="text-text-primary">{existing ? 'Finish draft' : 'New task'}</span>
      </nav>

      <PageHeader
        title={existing ? 'Finish draft' : 'New task'}
        description={`Goes into ${project.name}${project.businessName ? ` · ${project.businessName}` : ''}. You’re writing the brief (v0) on the client’s behalf.`}
        actions={
          <>
            <button
              type="button"
              disabled={pending}
              onClick={() => void save(true)}
              className={pillClass('light', 'lg')}
            >
              Save as draft
            </button>
            <button
              type="button"
              disabled={pending}
              aria-busy={pending || undefined}
              onClick={() => void save(false)}
              className={pillClass('dark', 'lg')}
            >
              <Icon icon={Check} size="sm" />
              {existing ? 'Submit task' : 'Create task'}
            </button>
          </>
        }
      />

      {formError ? <Banner tone="error">{formError}</Banner> : null}

      <div className="grid items-start gap-(--mbm-space-5) lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <div className="flex flex-col gap-(--mbm-space-5)">
          <Card padding="lg">
            <TaskTypePicker
              step={1}
              value={values.type}
              onChange={(type) => setValues((v) => withType(v, type))}
              locked={!!existing}
            />
          </Card>

          <Card padding="lg" className="flex flex-col gap-(--mbm-space-5)">
            <StepHeading
              step={2}
              title="Brief"
              description="What the team works from. This becomes version 0, and the client sees it in their app."
              aside={<Chip size="md">v0 · by agency</Chip>}
            />
            <BriefForm
              values={values}
              onChange={set}
              errors={errors}
              assets={<AssetUploader uploads={uploads} hint={ASSET_HINTS[values.type]} />}
            />
            {errors['assets'] ? <p className="text-body-small text-feedback-error">{errors['assets']}</p> : null}
          </Card>

          <Card padding="lg" className="flex flex-col gap-(--mbm-space-4)">
            <StepHeading
              step={3}
              title="Progress milestones"
              description="Stops on the progress slider. The team moves the slider and ticks the stops; the client sees the milestone reached."
            />
            <MilestoneEditor
              type={values.type}
              value={values.milestones}
              errors={errors}
              onChange={(milestones) => set({ milestones })}
            />
          </Card>
        </div>

        <div className="flex flex-col gap-(--mbm-space-5)">
          <Card className="flex flex-col gap-(--mbm-space-4)">
            <div className="flex flex-col gap-(--mbm-space-0-5)">
              <h2 className="text-heading-h3 text-text-primary">Triage</h2>
              <p className="text-body-small text-text-secondary">
                Optional now. Leave it empty to send the task to Task intake.
              </p>
            </div>
            <SegmentedControl
              label="Priority"
              options={[...PRIORITY_OPTIONS]}
              value={values.priority}
              onChange={(priority) => set({ priority })}
            />
            <div className="grid grid-cols-2 gap-(--mbm-space-3)">
              <DateField
                label="Due for review"
                value={values.dueDate}
                error={errors.dueDate}
                onChange={(dueDate) => set({ dueDate })}
              />
              <NumberStepper
                label="Effort (units)"
                value={values.workUnits}
                min={1}
                max={99}
                onChange={(workUnits) => set({ workUnits })}
              />
            </div>
            <p className="text-body-xsmall text-text-tertiary">
              1 unit = 2 h. The deadline is for the team; the client sees the publish date.
            </p>
          </Card>

          {existing ? null : (
            <NewTaskTeam
              team={values.team}
              onChange={(team) => set({ team })}
              onConfirmOverCapacity={() => set({ confirmOverCapacity: true })}
            />
          )}

          <Card className="flex flex-col gap-(--mbm-space-4)">
            <h2 className="text-heading-h3 text-text-primary">Client side</h2>
            <ToggleRow
              title="Visible to client"
              hint="Shows in their Work board and project"
              checked={values.clientVisible}
              onChange={(clientVisible) => set({ clientVisible })}
            />
            <ToggleRow
              title="Needs client approval"
              hint="They approve or ask for changes before it posts"
              checked={values.requiresClientApproval}
              onChange={(requiresClientApproval) => set({ requiresClientApproval })}
            />
          </Card>
        </div>
      </div>
    </>
  );
}
